import moment from "moment";
import { TFunction } from "i18next";
import { EventName, NotificationItem } from "../types";

const BANK_TRANSFER_EVENT: EventName = "wallet.bank-transfer-submitted";
const WALLET_TRANSACTION_CREATED_EVENT: EventName = "wallet.transaction-created";

type BankTransferStatus = "succeeded" | "pending" | "rejected" | "failed";

export type NotificationDetailRow = {
  label: string;
  value: string | null;
};

export type BankTransferDetails = {
  amount: string | null;
  senderName: string | null;
  bankName: string | null;
  uploadTransferProof: string | null;
  status: string | null;
};

export type WalletTransactionDetails = {
  amount: string | null;
  fullAmount: string | null;
  discountAmount: string | null;
  status: string | null;
};

const mapStatusKey = (status: string): string => {
  const normalized = status.toLowerCase() as BankTransferStatus;
  if (normalized === "succeeded") {
    return "notifications_bank_transfer_status_succeeded";
  }
  if (normalized === "pending") {
    return "notifications_bank_transfer_status_pending";
  }
  if (normalized === "rejected") {
    return "notifications_bank_transfer_status_rejected";
  }
  return "notifications_bank_transfer_status_failed";
};

const readString = (obj: Record<string, unknown>, key: string) => {
  const value = obj[key];
  return typeof value === "string" ? value : null;
};

const readNumericDisplay = (value: unknown): string | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value === "string" && value.length > 0) {
    return value;
  }
  return null;
};

const readAmountDisplay = (obj: Record<string, unknown>): string | null =>
  readNumericDisplay(obj.amount);

const readNestedRecord = (
  obj: Record<string, unknown>,
  key: string
): Record<string, unknown> | null => {
  const value = obj[key];
  if (typeof value === "object" && value !== null) {
    return value as Record<string, unknown>;
  }
  return null;
};

const readTransactionRecord = (
  payload: Record<string, unknown>
): Record<string, unknown> | null => readNestedRecord(payload, "transaction");

const readWalletTransactionDetails = (
  transaction: Record<string, unknown>,
  t: TFunction
): WalletTransactionDetails => {
  const statusRaw = readString(transaction, "status");
  return {
    amount: readNumericDisplay(transaction.amount),
    fullAmount: readNumericDisplay(transaction.full_amount),
    discountAmount: readNumericDisplay(transaction.discount_amount),
    status: statusRaw ? t(mapStatusKey(statusRaw)) : null,
  };
};

const readPreviewStrings = (payload: Record<string, unknown>) => {
  const preview = payload.preview;
  if (typeof preview !== "object" || preview === null) {
    return { title: null, body: null };
  }
  const record = preview as Record<string, unknown>;
  return {
    title: readString(record, "title"),
    body: readString(record, "body"),
  };
};

export const formatNotificationPresentation = (
  item: NotificationItem,
  t: TFunction
) => {
  const relativeTime = moment(item.occurredAt).fromNow();
  const payload = item.payload;
  const previewStrings = readPreviewStrings(payload);

  const title =
    previewStrings.title ??
    readString(payload, "title") ??
    t("notifications_event_unknown");

  const hasBankTransferMeta = Boolean(
    readString(payload, "sender_name") ||
      readString(payload, "bank_name") ||
      readString(payload, "upload_transfer_proof")
  );
  const hasBankTransferDetails =
    item.eventName === BANK_TRANSFER_EVENT || hasBankTransferMeta;

  const amountDisplay = readAmountDisplay(payload);
  const transactionRecord = readTransactionRecord(payload);
  const hasTransactionDetails =
    item.eventName === WALLET_TRANSACTION_CREATED_EVENT && transactionRecord !== null;

  const bankTransferDetails: BankTransferDetails | null = hasBankTransferDetails
    ? {
        amount: amountDisplay,
        senderName: readString(payload, "sender_name"),
        bankName: readString(payload, "bank_name"),
        uploadTransferProof: readString(payload, "upload_transfer_proof"),
        status: readString(payload, "status")
          ? t(mapStatusKey(readString(payload, "status") || "failed"))
          : null,
      }
    : null;

  const transactionDetails: WalletTransactionDetails | null =
    hasTransactionDetails && transactionRecord
      ? readWalletTransactionDetails(transactionRecord, t)
      : null;

  return {
    title,
    relativeTime,
    body:
      previewStrings.body ||
      readString(payload, "message") ||
      readString(payload, "title") ||
      t("notifications_detail_fallback"),
    hasBankTransferDetails,
    bankTransferDetails,
    hasTransactionDetails,
    transactionDetails,
  };
};

export const buildBankTransferDetailRows = (
  details: BankTransferDetails,
  t: TFunction
): NotificationDetailRow[] => [
  { label: t("amount"), value: details.amount },
  { label: t("sender_name"), value: details.senderName },
  { label: t("bank_name"), value: details.bankName },
  { label: t("upload_transfer_proof"), value: details.uploadTransferProof },
  { label: t("status"), value: details.status },
];

export const buildWalletTransactionDetailRows = (
  details: WalletTransactionDetails,
  t: TFunction
): NotificationDetailRow[] => [
  { label: t("amount"), value: details.amount },
  { label: t("notifications_transaction_full_amount"), value: details.fullAmount },
  { label: t("notifications_transaction_discount_amount"), value: details.discountAmount },
  { label: t("status"), value: details.status },
];
